import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { ThemeProvider, withTheme } from "styled-components";
import ScrollToTop from "../../components/library/ScrollToTop/ScrollToTop";
import CustomModal from "../../components/ui-library/CustomModal/CustomModal";
import SmartLogin from "../../components/smart/Login/Login";
import SmartAccountConfirmation from "../../components/smart/AccountConfirmation/AccountConfirmation";
import SmartPasswordRecovery from "../../components/smart/PasswordRecovery/PasswordRecovery";
import SmartPasswordDefine from "../../components/smart/PasswordDefine/PasswordDefine";
import SmartAdminRegistration from "../../components/smart/AdminRegistration/AdminRegistration";
import SmartNotice from "../../components/smart/Notice/Notice";
import LoggedOut from "../../components/library/LoggedOut/LoggedOut";
import ToastManager from "../../components/ToastManager/ToastManager";
import { emailRegex, modalType } from "../../modules/constants";
import {
  mapDispatchToPropsLogin,
  mapStateToPropsLogin,
} from "../../modules/login";
import {
  mapDispatchToPropsRecovery,
  mapStateToPropsRecovery,
} from "../../modules/recovery";
import { mapStateToPropsTheme } from "../../modules/theme";
import SmartAuditTrailComment from "../../components/smart/AuditTrailComment/AuditTrailComment";
import { useTranslation } from "react-i18next";
import CookieBanner from "../../components/library/CookieBanner/CookieBanner";
import Navigation from "../../components/library/Navigation/Navigation";
import Footer from "../../components/library/Footer/Footer";
import { refreshAppLanguage } from "../../i18n/helpers";

const StyledApp = styled.div`
  background-color: ${(props) => props?.theme?.bg};
  color: ${(props) => props?.theme?.fg};
  margin-top: 75px;
  padding: 30px 20px 30px 20px;
  -webkit-animation: fadein 1s; /* Safari, Chrome and Opera > 12.1 */
  -moz-animation: fadein 1s; /* Firefox < 16 */
  -ms-animation: fadein 1s; /* Internet Explorer */
  -o-animation: fadein 1s; /* Opera < 12.1 */
  animation: fadein 1s;

  @media print {
    background-color: transparent;
    padding: 0;
    margin: 0;
    height: 99%;
  }

  .no-animation {
    -webkit-animation: none !important;
    -moz-animation: none !important;
    -o-animation: none !important;
    animation: none !important;
  }
  @media (max-width: 320px) {
    margin-top: 63px;
  }

  @media only screen and (max-width: 768px),
    (min-device-width: 768px) and (max-device-width: 1024px) {
    padding: 30px 0px 30px 0px;
  }
`;

const ComponentWithLayout = (props) => {
  const {
    theme,
    authModalProps,
    content,
    onAuthModalClose,
    closeModalGoHome,
    closeModalLogout,
    modalSubtitle,
    modalButtonText,
    setOpenAuthModal,
    route,
    resetSubmittedChangeState,
    setLoginUsername,
    submittedRecover,
  } = props;

  const { t } = useTranslation();

  const [showNotice, setShowNotice] = useState(false);

  const hideFooter = [
    modalType.adminRegistrationProcess,
    modalType.changePassword,
    modalType.accountConfirmation,
  ];

  const closeModal = () => {
    resetSubmittedChangeState();
    closeModalGoHome();
  };

  const onLogoutClose = () => {
    refreshAppLanguage();
    onAuthModalClose();
  };

  useEffect(() => {
    if (authModalProps?.current === modalType.notice) {
      // this will prevent a black screen when the layout needs to change from ltr to rtl direction
      // https://ugnx-dmp.atlassian.net/browse/LCFAOD302-408
      setTimeout(() => {
        setShowNotice(true);
      }, [500]);
    }
  }, [authModalProps]);

  const onPasswordRecoveryClose = () => {
    onAuthModalClose();

    if (submittedRecover) {
      setLoginUsername("");
    } else {
      setOpenAuthModal(modalType.login, t("appText.login"), {
        fromPasswordRecovery: true,
      });
    }
  };

  const onLoginModalClose = () => {
    onAuthModalClose();
    setLoginUsername("");
  };

  return (
    <ThemeProvider theme={theme}>
      <ToastManager />
      <ScrollToTop>
        <Navigation isLogged={props.isLogged} withSidebar={props.withSidebar} />
        <CookieBanner />

        {!route.hideAuditTrailCommentButton && (
          <SmartAuditTrailComment route={route} />
        )}

        <StyledApp className="App" theme={theme}>
          {authModalProps?.current === modalType.login && (
            <CustomModal {...authModalProps} onClose={onLoginModalClose}>
              <SmartLogin
                openOnSecondStep={
                  content?.metadata?.fromPasswordRecovery || false
                }
              />
            </CustomModal>
          )}
          {authModalProps?.current === modalType.recoverPassword && (
            <CustomModal {...authModalProps} onClose={onPasswordRecoveryClose}>
              <SmartPasswordRecovery onDone={onPasswordRecoveryClose} />
            </CustomModal>
          )}
          {authModalProps?.current === modalType.loggedOut && (
            <CustomModal {...authModalProps} onClose={onLogoutClose}>
              <LoggedOut
                modalSubtitle={modalSubtitle}
                modalButtonText={modalButtonText}
                handleSubmit={() =>
                  setOpenAuthModal(modalType.login, t("appText.login"))
                }
              />
            </CustomModal>
          )}
          {authModalProps?.current === modalType.adminRegistrationProcess && (
            <CustomModal {...authModalProps} onClose={closeModalGoHome}>
              <SmartAdminRegistration showAdminReg={true} />
            </CustomModal>
          )}
          {authModalProps?.current === modalType.changePassword && (
            <CustomModal {...authModalProps} onClose={closeModal}>
              <SmartPasswordDefine showPwRecovery={true} />
            </CustomModal>
          )}
          {authModalProps?.current === modalType.accountConfirmation && (
            <CustomModal {...authModalProps} onClose={closeModalGoHome}>
              <SmartAccountConfirmation showAccountConfirmation={true} />
            </CustomModal>
          )}

          {authModalProps?.current === modalType.notice && showNotice && (
            <CustomModal {...authModalProps} onClose={closeModalLogout}>
              <SmartNotice />
            </CustomModal>
          )}
          {props.children}
        </StyledApp>
        {!hideFooter.includes(authModalProps?.current) && (
          <Footer withSidebar={props.withSidebar} isLogged={props.isLogged} />
        )}
      </ScrollToTop>
    </ThemeProvider>
  );
};

const ComponentWithTheme = (props) => {
  return (
    <ThemeProvider theme={props.theme}>
      <ToastManager />
      {props.children}
    </ThemeProvider>
  );
};

const mapStateToProps = (state) => {
  return {
    ...mapStateToPropsLogin(state),
    ...mapStateToPropsRecovery(state),
    ...mapStateToPropsTheme(state),
  };
};

export default {
  ComponentWithLayout: connect(mapStateToProps, {
    ...mapDispatchToPropsLogin,
    ...mapDispatchToPropsRecovery,
  })(withTheme(ComponentWithLayout)),
  ComponentWithTheme: connect(
    mapStateToProps,
    mapDispatchToPropsLogin
  )(withTheme(ComponentWithTheme)),
};
